// Shared design tokens + helpers for the Supply Chain Chronology page sections
// (PathwayTimeline, COOReasoning, EvidenceStack, MedicarePartB).
//
// The mockup uses a self-contained warm palette with several colors that are
// not in the global tokens.ts (e.g. SOFT #FBF8F2, LINE #E5DFD3). To match the
// mockup pixel-for-pixel we keep a local palette here rather than forcing the
// global T.* tokens (which also lacks a `T.text` token — known footgun).
import React from 'react';

export const MONO = "'IBM Plex Mono', 'Courier New', monospace";
export const SERIF = "'Playfair Display', serif";

// Warm palette (matches the page's existing cream/navy system).
export const C = {
  ink: '#1A1410',     // titles / primary text
  mute: '#6A5F52',    // secondary text
  dim: '#8A7F72',     // tertiary / captions / disclaimer
  line: '#E5DFD3',    // inner dividers
  border: '#D4CFC6',  // card borders
  bg: '#FFFFFF',
  soft: '#FBF8F2',    // expanded header / row striping
  cream: '#F2EDE4',
  red: '#C8160A',     // accent / high severity / divergent
  redBg: 'rgba(200,22,10,0.08)',
  gold: '#a16207',    // medium severity / ambiguous (text)
  goldAccent: '#D4A017',
  goldBg: 'rgba(212,160,23,0.12)',
  green: '#059669',   // agrees / confirmed
  greenBg: 'rgba(5,150,105,0.10)',
  blue: '#1A4DC8',    // US verdict
  blueBg: 'rgba(26,77,200,0.08)',
  greyBg: 'rgba(106,95,82,0.10)',
} as const;

// ── Number formatters (from the mockup) ─────────────────────────────────────
export function fmt(n: number | null | undefined, d = 0): string {
  if (n == null) return '—';
  return Number(n).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
}
export function fmtUsd(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n === 0) return '$0';
  if (n >= 1e9) return '$' + (n / 1e9).toFixed(2) + 'B';
  if (n >= 1e6) return '$' + (n / 1e6).toFixed(2) + 'M';
  if (n >= 1e3) return '$' + (n / 1e3).toFixed(1) + 'k';
  return '$' + fmt(n, 0);
}

// "2026Q1" -> "Q1 2026" for the Part B badge / labels.
export function fmtQuarter(q?: string | null): string {
  if (!q) return '';
  const m = /^(\d{4})Q([1-4])$/.exec(q.trim());
  return m ? `Q${m[2]} ${m[1]}` : q;
}

// Agreement tone for the PathwayTimeline accent bar + badge.
export type Agreement = 'confirmed' | 'divergent' | 'no_data';
export const AGREEMENT: Record<Agreement, { label: string; color: string; bg: string; border: string; icon: string }> = {
  confirmed: { label: 'CONFIRMED', color: '#059669', bg: 'rgba(5,150,105,0.1)', border: 'rgba(5,150,105,0.32)', icon: '✓' },
  divergent: { label: 'DIVERGENT', color: '#C8160A', bg: 'rgba(200,22,10,0.08)', border: 'rgba(200,22,10,0.35)', icon: '✦' },
  no_data: { label: 'UNKNOWN API', color: '#6A5F52', bg: '#F2EDE4', border: '#D4CFC6', icon: '○' },
};

export const TIER_COLOR: Record<string, string> = {
  HIGH: '#1A1410', MEDIUM: '#a16207', MODERATE: '#a16207', LOW: '#8A7F72',
};

// Medicare Part B anomaly flag → human label.
export const ANOMALY_LABEL: Record<string, string> = {
  FOREIGN_PRICE_COMPRESSION: 'Foreign Price Compression',
  MARGIN_ALERT: 'Reimburses Below Cost',
  PRICE_SPIKE: 'Price Spike',
  PRICE_DECLINE: 'Price Decline',
};

// Keyframes used across the chronology sections. Rendered once near the top of
// the chronology block. The project injects keyframes via local <style> blocks
// (see PipelineToast / StewardDataAnalyst), so we follow the same pattern.
export function ChronologyKeyframes(): React.ReactElement {
  return (
    <style>{`
      @keyframes chrSlideUp { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
      @keyframes chrDashFlow { to { stroke-dashoffset: -7; } }
    `}</style>
  );
}
